import { ViewConfig } from '../../../models/view-config.interface';
import { ConsigneeToursPaginatedService } from '@app/consignee/services/consignee-tours-paginated.service';
import { ToursService } from 'api/services';
import { AppStore } from '@app/stores/appStore';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { lastValueFrom } from 'rxjs';
import { ColumnCreator, ColumnValue } from '../column-creator';
import {
    TourData,
    DeliveryNoteData,
    TableRowData,
    hasKey,
    hasStatus,
    DeliveryNoteInTour,
    hasReadOnly,
} from '@app/shared/models/table-data-types';
import { SelectionStore, filterTourData } from '@app/stores/selectionStore';

export interface ConsigneeToursConfigDeps {
    consigneeToursPaginatedService: ConsigneeToursPaginatedService;
    toursService: ToursService;
    actionsService: ConsigneeViewActionsService;
    appStore: InstanceType<typeof AppStore>;
    selectionStore: InstanceType<typeof SelectionStore>;
}

export class ConsigneeToursConfig {
    static create(deps: ConsigneeToursConfigDeps): ViewConfig {
        return {
            id: 'consignee-tours',
            name: 'Tours',
            translationKey: 'OUTGOING-GOODS.TOGGLER.TOURS',
            type: 'tours',
            userRole: 'consignee',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey = deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    return deps.consigneeToursPaginatedService.loadConsigneeToursPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        selectableCondition: (item: TableRowData) => {
                            const isNotClosed =
                                hasStatus(item) && item.status !== 'closed';
                            const isNotReadOnly =
                                !hasReadOnly(item) || !item.read_only;
                            return isNotClosed && isNotReadOnly;
                        },
                    }),
                    ColumnCreator.details(),
                    ColumnCreator.tourNumber({
                        header: 'INCOMING-GOODS.TABLE.TOURS.HEADERS.TOUR-KEY',
                    }),
                    ColumnCreator.externalTourName(),
                    ColumnCreator.actions({
                        width: '120px',
                        configuration: (
                            row: TableRowData,
                            value: ColumnValue
                        ) =>
                            ({
                                type: 'bundle',
                                showDeliveryNotesCount: true,
                                deliveryNotesCountClickable: false,
                                context: 'consignee',
                            }) as TableActionsConfig,
                    }),
                    ColumnCreator.carrierName({
                        header: 'INCOMING-GOODS.TABLE.CARRIER-NAME',
                    }),
                    ColumnCreator.dateCheckout({
                        header: 'INCOMING-GOODS.TABLE.TOURS.HEADERS.SENT-DATE',
                    }),
                    ColumnCreator.status({
                        header: 'INCOMING-GOODS.TABLE.TOURS.HEADERS.STATUS',
                        showDiscrepanciesIcon: true,
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    expandable: true,
                },
                expandedRowConfig: {
                    expandableCondition: (item: TourData) =>
                        (item.deliveryNoteKeys?.length || 0) > 0,
                    tableConfig: {
                        columns: [
                            ColumnCreator.actions({
                                width: '200px',
                                configuration: (
                                    row: TableRowData,
                                    value: ColumnValue,
                                    parentRow?: TableRowData
                                ) =>
                                    ({
                                        type: 'deliveryNote',
                                        showView: true,
                                        showDownload: true,
                                        showDeliveryNotesCount: false,
                                        showAddNotes: true,
                                        downloadEndpoint: (
                                            deliveryNote: TableRowData,
                                            parentRow?: TableRowData
                                        ) => {
                                            return deps.actionsService.downloadDeliveryNote(
                                                deliveryNote as DeliveryNoteData
                                            );
                                        },
                                        viewEndpoint: (
                                            deliveryNote: TableRowData,
                                            parentRow?: TableRowData
                                        ) => {
                                            return deps.actionsService.viewDeliveryNote(
                                                deliveryNote as DeliveryNoteData
                                            );
                                        },
                                        addNotesEndpoint: (
                                            item: TableRowData,
                                            parentRow?: TableRowData
                                        ) =>
                                            deps.actionsService.addFineDiscrepancy(
                                                item as DeliveryNoteData,
                                                parentRow as TourData
                                            ),
                                        bundleKey:
                                            parentRow && hasKey(parentRow)
                                                ? parentRow._key
                                                : undefined,
                                        bundleStatus:
                                            parentRow && hasStatus(parentRow)
                                                ? parentRow.status
                                                : undefined,
                                        organizationSiteKey:
                                            parentRow?.orgSiteKey,
                                        context: 'consignee',
                                    }) as TableActionsConfig,
                            }),
                            ColumnCreator.deliveryNoteNumber(),
                            ColumnCreator.orderNumber(),
                            ColumnCreator.referenceNumber({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                                orgType: 'consignee',
                                readOnly: true, // Consignees typically can't edit reference numbers
                            }),
                            ColumnCreator.dateAnnounced({
                                header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ADVISED-DATE',
                            }),
                            ColumnCreator.dateReceived({
                                header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.RECEIVED-DATE',
                            }),
                            ColumnCreator.status({
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE',
                            }),
                            ColumnCreator.discrepancies(),
                            ColumnCreator.palletTickets(),
                            ColumnCreator.originalConsignor(),
                            ColumnCreator.originalConsignorGln(),
                        ],
                        apiConfig: {
                            loadData: async (
                                parentRow: TourData,
                                organizationSiteKey: string,
                                customParams?: Record<string, unknown>
                            ) => {
                                try {
                                    const result =
                                        deps.toursService.getConsigneeTour({
                                            tourKey: parentRow._key,
                                            organizationSiteKey:
                                                parentRow.orgSiteKey,
                                        });
                                    const tourDetails =
                                        result instanceof Promise
                                            ? await result
                                            : await lastValueFrom(result);

                                    // Transform delivery notes to include required properties
                                    return (
                                        tourDetails.deliveryNotes || []
                                    ).map(
                                        (deliveryNote: DeliveryNoteInTour) => ({
                                            ...deliveryNote,
                                            orgSiteKey: parentRow.orgSiteKey,
                                            read_only:
                                                parentRow.status === 'closed' ||
                                                parentRow.read_only ||
                                                false,
                                            status:
                                                deliveryNote.dateReceived &&
                                                deliveryNote.dateReceived > 0
                                                    ? 'closed'
                                                    : tourDetails.status,
                                        })
                                    );
                                } catch (error) {
                                    return [];
                                }
                            },
                            organizationSiteKeyResolver: (row: TourData) =>
                                row.orgSiteKey,
                            customParams: (row: TourData) => ({
                                tourKey: row._key,
                                tourNumber: row.tourNumber,
                            }),
                        },
                        loadingMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.LOADING-DELIVERY-NOTES',
                        emptyMessage:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.NO-DELIVERY-NOTES',
                        tableClass: 'expanded-delivery-notes-table',
                        maxHeight: '400px',
                    },
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'tourNumber',
                        label: 'OUTGOING-GOODS.TOUR-VIEW.TABLE-HEADER.TOUR-NO',
                        translationKey:
                            'OUTGOING-GOODS.TOUR-VIEW.TABLE-HEADER.TOUR-NO',
                    },
                    {
                        keyName: 'externalTourName',
                        label: 'SPEDITEUR.TABLE.HEADERS.TOUR-NAME',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.TOUR-NAME',
                    },
                    {
                        keyName: 'carrierName',
                        label: 'OUTGOING-GOODS.TOUR-VIEW.TABLE-HEADER.CARRIER-NAME',
                        translationKey:
                            'OUTGOING-GOODS.TOUR-VIEW.TABLE-HEADER.CARRIER-NAME',
                    },
                    {
                        keyName: 'checkoutDate',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
                    },
                ],
                showClosed: false,
                enableAdvancedFilters: false,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'name',
                    'status',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'tourNumber',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: false,
                canUpdate: true,
                canDelete: false,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'checkinTourDeliveryNotes',
                        label: 'Check-in Folders',
                        translationKey: 'INCOMING-GOODS.BUTTONS.CHECKIN-FOLDER',
                        svgIcon: 'folder-checkout',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => {
                            if (!selection || selection.length === 0)
                                return true;
                            // Disable if no delivery notes or not all from same org site
                            const firstOrgSiteKey = selection[0]?.orgSiteKey;
                            return !selection.every(
                                (tour: TourData) =>
                                    tour.orgSiteKey === firstOrgSiteKey &&
                                    (tour.deliveryNoteKeys?.length || 0) > 0
                            );
                        },
                        handler: async (item, selection) => {
                            const currentSelection = deps.selectionStore.selectedItems();
                            const context = deps.selectionStore.contextData();

                            // Filter to only tour data items
                            const tourItems = filterTourData(currentSelection);

                            // Call the check-in service with properly typed tour items
                            if (tourItems.length > 0) {
                                await deps.actionsService.checkinTourDeliveryNotes(
                                    tourItems
                                );
                            } else {
                            }
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.CHECKIN-FOLDER',
                    },
                    {
                        id: 'scanWallet',
                        label: 'Scan Wallet',
                        translationKey: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                        icon: 'qr_code_scanner',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => true,
                        disabled: (item, selection) => false,
                        handler: async () => {
                            deps.actionsService.scanWallet();
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                    },
                ],
            },
        };
    }
}
